<template>
  <div>
    <PaymentLoading v-if="isLoading" />
    <template v-else-if="isConfirmed">
      <OrderResult
        image-url="/img/order/insurance-apply-success.png"
        image-title="주문완료!"
        :isInsuranceOrderResult="true"
      >
        <template #order-detail>
          <h1 class="order-detail__title">
            {{ applyOrderDetailData.destinationCountryName }} 해외여행보험
            {{
              getInsurancePeriodDays(
                applyOrderDetailData?.insuranceStartDateTime,
                applyOrderDetailData?.insuranceEndDateTime
              )
            }}
          </h1>

          <h2 class="order-detail__coverage__title">
            보장 플랜: {{ applyOrderDetailCoverageData }}
          </h2>

          <div class="tw:border-b tw:border-b-[#E5E5EC] tw:mt-3!" />

          <div class="tw:mt-4! order-detail">
            <div
              v-for="row in insuranceDetailData(applyOrderDetailData)"
              :key="row.label"
              class="tw:flex tw:typo-text-m pb-4!"
            >
              <p class="tw:w-[80px] tw:text-usdg-color-gray-600">{{ row.label }}</p>
              <p>{{ row.value }}</p>
            </div>
          </div>
        </template>
      </OrderResult>
      <UsdgBottomCta custom-class="tw:mt-auto!">
        <div class="tw:flex tw:gap-3">
          <CtaButton
            size="xl"
            type="tertiary"
            text="주문상세보기"
            w-full
            @click-button="goUserHistoryPage"
          />
          <CtaButton
            size="xl"
            text="홈으로 가기"
            w-full
            @click-button="goToMain"
          />
        </div>
      </UsdgBottomCta>
    </template>

    <template v-else-if="isShowFailModal">
      <Fail type="insurance" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { useBFCacheGuard } from '@/uComposables/order/useBFCacheGuard'
import type { PostOrderApprovePayload } from '@/uTypes/order/api'
import Fail from './fail.vue'
import { dayjs } from 'element-plus'
import { navigateWithHref } from '@/utils/_common/navigation.js'
import { ALLOW_ORDER_SUCCESS } from '@/constants/order'
import { isNativeShellApp } from '@/utils/appBridgeGate'

definePageMeta({
  layout: 'center',
  middleware: ['naver-ads', 'kakao-pixel']
})

const { postMemberMultiPrdOrderApprove } = useOrderAPI()

const applyOrderDetailData = ref<InsuranceApplyRes | null>(null)
const applyOrderDetailCoverageData = ref(null)
const { commaWon } = useUi()

const route = useRoute()
const router = useRouter()

const orderGroupId = computed(() => {
  const value = route.query.orderGroupId ?? route.query.orderId
  return typeof value === 'string' ? value : ''
})
const paymentKey = computed(() =>
  typeof route.query.paymentKey === 'string' ? route.query.paymentKey : ''
)
const amount = computed(() => (typeof route.query.amount === 'string' ? route.query.amount : ''))

const { guardBFCacheSuccessPage } = useBFCacheGuard(orderGroupId)

const isLoading = ref(true)
const isConfirmed = ref(false)
const isShowFailModal = ref<boolean>(false)
const modalTitle = ref<string>('')

const handleFail = (message?: string) => {
  isLoading.value = false
  isConfirmed.value = false
  isShowFailModal.value = true
  modalTitle.value = message || '가입에 실패했습니다.'
}

const fetchInsuranceContract = async (id: string) => {
  const { getMyInsuranceContract } = useInsuranceAPI()
  const applyRes = await getMyInsuranceContract(id)

  applyOrderDetailData.value = applyRes.data
  applyOrderDetailCoverageData.value = applyRes.data.planName
  isConfirmed.value = true
}

const goUserHistoryPage = () => {
  if (isNativeShellApp()) {
    // 네이티브 구매정보 → 해외여행보험 탭
    window.flutter_inappwebview?.callHandler('openNativeScreen', {
      screen: 'purchaseHistory',
      tab: 'insurance'
    })
    return
  }

  router.push('/user/history?tab=insurance')
}

// 결제완료 [홈으로 가기] — `goUserHistoryPage` 와 동일 패턴
// (`pages/order/success/guest.vue` 의 `goToMain` 참고).
const goToMain = () => {
  if (isNativeShellApp()) {
    window.flutter_inappwebview?.callHandler('openNativeScreen', {
      screen: 'home'
    })
    return
  }

  navigateWithHref('/')
}

const insuranceDetailData = (insurance: InsuranceApplyRes) => {
  return [
    {
      label: '증권번호',
      value: insurance?.policyNo || ''
    },
    {
      label: '여행국가',
      value: insurance?.destinationCountryName
    },
    {
      label: '보험기간',
      value: `${dayjs(insurance?.insuranceStartDateTime).format('YY.MM.DD HH시')} ~ ${dayjs(insurance?.insuranceEndDateTime).format('YY.MM.DD HH시')}`
    },
    {
      label: '피보험자',
      value:
        insurance?.insured?.length > 1
          ? `${insurance?.insured?.find((i) => i.contractorFlag)?.name || insurance?.insured?.find((i) => i.contractorFlag)?.englishName} 외 ${insurance?.insured?.length - 1}명`
          : insurance?.insured?.find((i) => i.contractorFlag)?.name ||
            insurance?.insured?.find((i) => i.contractorFlag)?.englishName
    },
    {
      label: '결제금액',
      value: commaWon(insurance?.settledAmount)
    }
  ]
}

const getInsurancePeriodDays = (startDateTime?: string, endDateTime?: string) => {
  if (!startDateTime || !endDateTime) {
    return ''
  }

  const start = dayjs(startDateTime)
  const end = dayjs(endDateTime)

  if (!start.isValid() || !end.isValid()) {
    return ''
  }

  return `${end.diff(start, 'day') + 1}일`
}

onMounted(async () => {
  const isBlocked = guardBFCacheSuccessPage()

  if (isBlocked) {
    return
  }

  const id = orderGroupId.value

  if (!id) {
    handleFail('주문 정보가 올바르지 않습니다.')
    return
  }

  try {
    if (paymentKey.value && amount.value) {
      const parsedAmount = Number(amount.value)

      if (!Number.isFinite(parsedAmount)) {
        handleFail('결제 금액 정보가 올바르지 않습니다.')
        return
      }

      const payload: PostOrderApprovePayload = {
        orderGroupId: id,
        paymentKey: paymentKey.value,
        amount: parsedAmount
      }

      const res = await postMemberMultiPrdOrderApprove(payload)

      if (!res?.success) {
        handleFail('가입에 실패했어요. 결제하신 금액은 자동 환불됩니다.')
        return
      }

      sessionStorage.setItem(ALLOW_ORDER_SUCCESS, 'true')

      // approve 재호출 방지
      await router.replace({
        path: route.path,
        query: {
          orderGroupId: id
        }
      })
    }

    await fetchInsuranceContract(id)
  } catch (e: any) {
    handleFail(e?.error?.message || '보험 가입 정보를 불러오지 못했습니다.')
  } finally {
    const { getMyInsuranceContract } = useInsuranceAPI()

    isLoading.value = false
    const applyRes = await getMyInsuranceContract(id)
    useAirbridge().trackInsuranceOrderSuccess(applyRes.data)
  }
})
</script>

<style scoped lang="scss">
.loading-status {
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  .loading-status__title {
    font-size: 18px;
    font-weight: 600;
    color: $gray-700;
  }
  .loading-status__desc {
    padding: 8px;
    color: $gray-400;
  }
}
.fail-text {
  font-size: 14px;
  color: $gray-400;
  text-align: center;
}
.order-detail {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__coverage__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    line-height: 26px;
    margin-bottom: 4px;
  }
}
</style>
